import type { Ref } from 'vue'
import type { ItemRow } from '~~/shared/items/itemRow'

export interface ExplorerKeyboardDeps {
  /** The panel root. Keys act only while focus is inside it. */
  panel: Ref<HTMLElement | null>
  selectedCount: () => number
  focusedItem: () => ItemRow | null
  isExpanded: (id: string) => boolean
  moveSelection: (delta: 1 | -1) => void
  expandFolder: (item: ItemRow) => Promise<void>
  collapseFolder: (id: string) => void
  /** Enter — the context menu's "Open", never the store's folder-path navigate (MAR-957). */
  open: (item: ItemRow) => void
  /** F2. */
  rename: (item: ItemRow) => void
  /** Delete / Cmd+Backspace — asks first, through the same path as the header's button. */
  deleteSelected: () => Promise<void>
  toggleSelectAll: () => void
}

/**
 * The documents sidebar's keyboard map (MAR-948 — split out of `FileExplorer.vue`).
 *
 * Scoped to the browser panel so it never hijacks app-wide text shortcuts: the listener sits on the
 * panel root, acts only while focus is inside it, and ignores keys typed into an input.
 */
export function useExplorerKeyboard(deps: ExplorerKeyboardDeps) {
  function panelHasFocus(): boolean {
    // `panel` is only ever set by a client-side mount, so it is the client check too — an
    // `import.meta.client` here is undefined outside a Nuxt build and left F2/arrow handling
    // unreachable to any test.
    const panel = deps.panel.value
    if (!panel) return false
    const active = document.activeElement
    return active != null && (panel === active || panel.contains(active))
  }

  async function onKeyDown(e: KeyboardEvent) {
    const tag = (e.target as HTMLElement)?.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA') return
    if (!panelHasFocus()) return

    // Arrow navigation over the flattened visible tree.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      deps.moveSelection(e.key === 'ArrowDown' ? 1 : -1)
      return
    }
    if (e.key === 'ArrowRight') {
      const item = deps.focusedItem()
      if (item?.type === 'folder') {
        e.preventDefault()
        if (deps.isExpanded(item.id)) deps.moveSelection(1)
        else await deps.expandFolder(item)
      }
      return
    }
    if (e.key === 'ArrowLeft') {
      const item = deps.focusedItem()
      if (item?.type === 'folder' && deps.isExpanded(item.id)) {
        e.preventDefault()
        deps.collapseFolder(item.id)
      }
      return
    }

    if (e.key === 'Enter' || e.key === 'F2') {
      const item = deps.focusedItem()
      if (item) {
        e.preventDefault()
        if (e.key === 'Enter') deps.open(item)
        else deps.rename(item)
      }
      return
    }

    // Delete: require Cmd/Meta (mac) or plain Delete key (not bare Backspace), so an accidental
    // Backspace can't silently delete documents.
    const isDeleteCombo = e.key === 'Delete' || ((e.metaKey || e.ctrlKey) && e.key === 'Backspace')
    if (isDeleteCombo && deps.selectedCount() > 0) {
      e.preventDefault()
      await deps.deleteSelected()
      return
    }

    // Cmd/Ctrl+A: select all (only inside the panel).
    if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
      e.preventDefault()
      deps.toggleSelectAll()
    }
  }

  return { onKeyDown }
}
